import { useEffect, useRef } from 'react';

import { IEventSettings } from '@interfaces/event';
import { MytaverseLogger } from '@helpers/logger';
import { isAIEnabledForEventSettings } from './aiEnabled';
import { stopAssistantAudio } from './providers/globals';

/**
 * Tears the assistant's audio down when the experience's AI gate flips off
 * mid-session.
 *
 * The CMS AI toggles arrive live over `EVENT_SETTINGS_UPDATED` (MV-1816), so
 * `HomeBottomBar`'s gate can flip while the participant is inside the
 * experience — which *unmounts* the whole `AssistantProvider` subtree. That
 * unmount is not a teardown: `globals.visualizerInstance` and
 * `globals.assistantTornDown` are module-level state that outlives the tree, so
 * audio mid-playback keeps going and an in-flight Polly fetch still builds a
 * fresh visualizer from the unmounted tree (see `stopAssistantAudio`). This
 * makes the transition do exactly what leaving the event page does.
 *
 * Re-enabling keeps working: the next `AssistantProvider` mount re-arms
 * `globals.assistantTornDown`. `globals.hasGreetedLandingGlob` is deliberately
 * left alone — it is page-lifetime state, so a disable/enable cycle does not
 * replay the landing greeting.
 *
 * Must be called from a component that stays mounted across the transition
 * (`MytaverseEventProvider`), never from inside the gated subtree.
 *
 * @param currentEventId - Experience the participant is inside; falsy while none is loaded.
 * @param settings - `settings` of the loaded experience, as last broadcast.
 */
export const useAssistantTeardownOnDisable = (
  currentEventId: string | null | undefined,
  settings: IEventSettings | undefined,
): void => {
  const isAIEnabled = isAIEnabledForEventSettings(currentEventId, settings);
  // `undefined` until the first effect run, so first load never counts as a
  // transition — neither an already-disabled experience nor an already-enabled
  // one tears anything down on mount.
  const prevIsAIEnabledRef = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    const wasEnabled = prevIsAIEnabledRef.current;
    prevIsAIEnabledRef.current = isAIEnabled;

    if (!wasEnabled || isAIEnabled) {
      return;
    }

    MytaverseLogger.debug(
      'AI assistant disabled mid-session — stopping assistant audio',
      { eventId: currentEventId },
    );
    stopAssistantAudio();
  }, [isAIEnabled, currentEventId]);
};
